import React from 'react'
import { Link } from 'react-router-dom'
import { AppContextProps } from '../../hooks/useApp'
import { RelayData } from '../../types/charts'
import { OverrideSchedule, TimeString } from '../../types/models'
import { DashboardViewProps, RelayBarWidgetConfig, TestViewProps, Widget, WidgetData } from './Widget'
import { QuestionMark, Settings } from '@mui/icons-material'
import { Typography, Button, IconButton, useTheme } from '@mui/material'
import { Box, Stack } from '@mui/system'
import { DateTime } from 'luxon'
import { Trans } from 'react-i18next'
import { AutoManualLed } from '../../components/atoms/AutoManualLed'
import { ExpanderButton } from '../../components/atoms/ExpanderButton'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'
import { RelayBar } from '../../components/charts/RelayBar'
import { callAxios } from '../../hooks/useAxios'
import { RELAY_TIMEOUT } from '../../types/constants'
import { OptimizerSchedule } from '../../types/ports'
import { now } from '../../utils/time'
import { Schedule } from '../../components/Schedule'
import { RelayPortControl } from '../ports/RelayPortControl'
import { OptimizerControlPortControl } from '../ports/OptimizerControlPortControl'
import { SystemControl } from '../SystemControl'
import { ApplianceConfigRelay, ApplianceData } from '../appliances/Appliance'
import { SocketMessage, isApplianceMessage, isUpdateMessage } from '../../types/messaging'

export class RelayBarWidget extends Widget<{ relays: RelayData<TimeString>[] }, RelayBarWidgetConfig> {

  open: boolean
  relayState?: boolean

  constructor(data: WidgetData, control: SystemControl, app: AppContextProps) {
    super(data, control, app)
    this.open = false
    this.relayState = undefined
  }

  get Icon() {
    return this.appliances.length ? this.appliances[0].Icon : QuestionMark
  }

  get width(): number {
    return 4
  }

  get height(): number {
    return 2
  }

  get textColor(): string {
    return 'statsText.main'
  }

  get bgColor(): string {
    return 'statsBackground.main'
  }

  /**
   * Figure out if manual control is available.
   */
  get manual(): boolean {
    return this.appliance.getConfig('strategy') === 'MANUAL'
  }

  dashboardTitle(firstOfKind: boolean): string | undefined {
    if (firstOfKind) return this.app.t('Appliances')
    return undefined
  }

  dataUrls(): Record<string, string> {
    const [from] = this.dateRange()
    const ret: Record<string, string> = {
      relays: `/history/relay/${this.controls[0].port.id}?from=${from}`
    }
    return ret
  }

  relayControl(): RelayPortControl {
    return this.findOneControl('RELAY', 'EVSE') as RelayPortControl
  }

  optimizerControl(): OptimizerControlPortControl {
    return this.findOneControl('OPTIMIZER_CONTROL') as OptimizerControlPortControl
  }

  draw(props: DashboardViewProps): React.ReactElement {
    const Icon = this.Icon

    const control = this.relayControl()
    const optimizer = this.optimizerControl()
    const errorFlags = this.errorFlags()
    const connectionStatus = control.getConnectionStatus()
    const isEvse = control.port.type === 'EVSE'
    // Commanded vs. charger-acknowledged current limits — shown for all EVSE driver types.
    const commandCurrent = isEvse ? control.getValue('configured_current_limit') as number | undefined : undefined
    const ackedCurrent = isEvse ? control.getValue('actual_current_limit') as number | undefined : undefined
    // charging_power is only reported by EVSE drivers that support it (e.g. Sungrow, not Charlie).
    const chargingPower = isEvse
      ? control.getValue('charging_power') as number | null
      : null
    const isOffline = control.getValue('is_on') === undefined && control.getValue('actual_current_limit') === undefined
    const hasErrors = errorFlags.length > 0
    const isOn = control.isOn()
    const hasData = this.data && 'relays' in this.data && this.data.relays.length > 0
    const data = hasData ? this.data?.relays.map(e => ({ ...e, timestamp: DateTime.fromISO(e.timestamp).toMillis() })) || [] : []

    const [from, to] = this.dateRange()

    let overriding = false
    let left = 0
    let schedule: OptimizerSchedule = []
    if (!this.manual && this.appliances && this.appliances.length) {
      schedule = optimizer.port.values.schedule || []
      const override: OverrideSchedule | undefined = this.appliances[0].getConfig('override')
      if (override && override.schedule && override.schedule.length > 0) {
        const start = now()
        const last = override.schedule[override.schedule.length - 1]
        const end = DateTime.fromISO(last[0])
        left = Math.round(end.diff(start, ['minutes']).minutes)
        if (left > 0) {
          overriding = true
        }
      }
    }

    const onToggle = () => {
      if (control.isOn()) {
        control.turnOff()
      } else {
        control.turnOn()
      }
    }

    const onForce = async (hours: number, value: boolean) => {
      const now = {
        ...DateTime.utc().plus({ minutes: -2 }).toObject(),
        millisecond: 0,
        second: 0
      }

      const start: TimeString = DateTime.fromObject(now, { zone: 'UTC' }).toISO() as TimeString
      const end: TimeString = DateTime.fromObject(now, { zone: 'UTC' }).plus({ minutes: hours * 60 + 2 }).toISO() as TimeString

      const config = {
        ...this.appliances[0].config,
        override: {
          command: 'OPTIMIZER_CONTROL_CMD_CMD_SCHEDULE',
          schedule: [
            [start, { name: 'is_on', value }],
            [end, { name: 'is_on', value: !value }],
          ]
        }
      }

      const res = await callAxios<ApplianceData>({
        method: 'PATCH',
        url: `/appliances/${this.appliances[0].id}`,
      }, { config })

      if (res.success) {
        this.appliances[0].setConfig('override', (res.data.config as ApplianceConfigRelay).override)
        props.refresh()
      } else {
        this.app.addError(res)
      }
    }

    const onForceCancel = async () => {
      const config = {
        ...this.appliances[0].config,
        override: {
          command: 'OPTIMIZER_CONTROL_CMD_CMD_SCHEDULE',
          schedule: []
        }
      }

      const res = await callAxios<ApplianceData>({
        method: 'PATCH',
        url: `/appliances/${this.appliances[0].id}`,
      }, { config })

      if (res.success) {
        this.appliances[0].setConfig('override', (res.data.config as ApplianceConfigRelay).override)
        props.refresh()
      } else {
        this.app.addError(res)
      }
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const theme = useTheme()

    return (
      <Box sx={{
        backgroundColor: this.bgColor,
        boxShadow: theme.boxShadow.main,
        borderRadius: '1.25rem',
        height: '100%'
      }} data-cy={`box-${this.getName()}`}>
        <Stack>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '2.5rem auto 1rem 4rem 2rem',
            alignItems: 'center',
            paddingLeft: '1rem',
            height: '2rem',
          }}>
            <Icon sx={{ color: isOffline ? 'gray' : this.textColor }}/>
            <Typography sx={{ color: isOffline ? 'gray' : this.textColor }}>{this.getName()}{connectionStatus !== 'Connected' && ' (' + connectionStatus + ')'}</Typography>
            <Box>
              <AutoManualLed disabled={isOffline} manual={this.manual} error={hasErrors}/>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <CustomSwitch
                data-cy="switch-Toggle"
                timeout={RELAY_TIMEOUT}
                timeoutMessage={this.app.t('It seems that the relay is unreachable at the moment.')}
                disabled={!this.manual || isOffline}
                checked={isOn}
                onClick={onToggle}
              />
            </Box>
            <ExpanderButton open={this.open} disabled={props.upgrading} color={this.textColor} onToggle={() => {
              this.open = !this.open
              props.refresh()
            }}/>
          </Box>

          <Box sx={{ px: 4, pb: 2 }}>
            { !hasErrors &&
              !props.upgrading &&
                <RelayBar from={from} to={to} relay={data}/>
            }
            { hasErrors &&
                <Box sx={{ color: 'error.light', pl: 3, m: 0, fontSize: '85%' }}>{errorFlags.join(' ')}</Box>
            }
            { props.upgrading &&
                <Box sx={{ ml: 3, color: 'primary.light' }}><Trans>Upgrading...</Trans></Box>
            }
          </Box>

          {this.open && !props.upgrading &&
            <Box sx={{ mx: 2, mb: 2 }}>
              {
                !this.manual &&
                !overriding &&
                  <Box sx={{ textAlign: 'center ' }}>
                    <Button sx={{ m: 1 }} onClick={() => onForce(3, true)} size="small" variant="text">
                      <Trans>Force On 3h</Trans>
                    </Button>
                    <Button sx={{ m: 1 }} onClick={() => onForce(3, false)} size="small" variant="text">
                      <Trans>Force Off 3h</Trans>
                    </Button>
                  </Box>
              }
              <Box>
                {
                  this.appliances.map(app =>
                    <React.Fragment key={app.id}>
                      {app.widget(this.manual, overriding || this.appliances[0].getConfig('strategy') === 'CALENDAR', props.refresh)}
                    </React.Fragment>
                  )
                }
              </Box>
              {
                !this.manual &&
                overriding && left > 0 &&
                  <Box>
                    <Typography sx={{ ml: 5, mb: 2 }}>
                      {this.app.t('Override left: {hours}h {minutes}min').replace('{hours}', Math.floor(left / 60) + '').replace('{minutes}', (left % 60) + '')}
                    </Typography>
                    <Button sx={{ ml: 5 }} onClick={() => onForceCancel()} size="small" variant="text"><Trans>Cancel Override</Trans></Button>
                  </Box>
              }
              {
                isEvse &&
                  <Box sx={{ mt: 2, ml: 5 }}>
                    <span>{this.app.t('EvseCommandCurrent')}: {commandCurrent ?? '–'} A</span>
                    <span style={{ marginLeft: '1.5rem' }}>{this.app.t('EvseAckedCurrent')}: {ackedCurrent ?? '–'} A</span>
                  </Box>
              }
              {
                chargingPower !== null && chargingPower !== undefined &&
                  <Box sx={{ mt: 2, ml: 5 }}>
                    {this.app.t('Charging power now')}: {(chargingPower / 1000).toFixed(1)} kW
                  </Box>
              }
              {
                !this.manual &&
                  <Box sx={{ mt: 2, ml: 5 }}>
                    <Trans>Current plan</Trans>:
                    <Schedule sx={{ mt: 1 }} schedule={schedule}/>
                  </Box>
              }
              <Link to={`/appliances/${this.appliances[0].id}`}>
                <Stack direction="row-reverse" sx={{ mt: 2, mr: 3, cursor: 'pointer' }}>
                  <IconButton sx={{ transform: 'translateY(-25%)', color: this.textColor }}>
                    <Settings />
                  </IconButton>
                  <Typography sx={{ textDecoration: 'underline', color: this.textColor }}>
                    <Trans>Settings</Trans>
                  </Typography>
                </Stack>
              </Link>
            </Box>
          }
        </Stack>
      </Box>
    )
  }

  drawTestView(props: TestViewProps) {
    const control = this.relayControl()
    const isOn = control.isOn()

    const onToggle = () => {
      if (isOn) {
        control.turnOff()
      } else {
        control.turnOn()
      }
    }

    return <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ mb: 2 }}>
      { isOn &&
        <Box sx={{
          margin: 'auto',
          width: '5rem',
          height: '2rem',
          border: '1px solid lime',
          backgroundColor: 'green',
          color: this.textColor,
          textAlign: 'center',
          lineHeight: '2rem',
          borderRadius: '0.4rem'
        }} onClick={onToggle}><Trans>ON</Trans></Box>
      }
      { !isOn &&
        <Box sx={{
          margin: 'auto',
          width: '5rem',
          height: '2rem',
          border: '1px solid gray',
          backgroundColor: '#111',
          color: this.textColor,
          textAlign: 'center',
          lineHeight: '2rem',
          borderRadius: '0.4rem'
        }} onClick={onToggle}><Trans>OFF</Trans></Box>
      }
      </Box>
      <Box>
        <Trans>Click the switch to test the relay</Trans>
      </Box>
      <CustomSwitch
        data-cy="switch-Toggle"
        timeout={RELAY_TIMEOUT}
        timeoutMessage={this.app.t('It seems that the relay is unreachable at the moment.')}
        checked={!!isOn}
        onClick={onToggle}
      />

    </Box>
  }

  needRefreshOnMessage(msg: SocketMessage): boolean {

    if (isUpdateMessage(msg) && this.hasPortId(msg.port_id)) {
      if ('is_on' in msg.values && msg.values.is_on !== this.relayState) {
        return true
      }
      if ('schedule' in msg.values) {
        return true
      }
    }

    // This is temporary solution and should be replaced with comprehensive in-memory appliance data.
    if (isApplianceMessage(msg)) {
      const i = this.appliances.map(a => a.id).indexOf(msg.id)
      if (i >= 0 && msg.config) {
        Object.assign(this.appliances[i].config, msg.config)
        return true
      }
    }

    return false
  }

  async beforeRefresh(): Promise<void> {
    if (this.data?.relays) {
      const schedule = this.optimizerControl().port.values.schedule
      if (schedule && schedule.length) {
        schedule.forEach(([time, { reason, value }]) => {
          this.data?.relays.push({
            source: 'schedule',
            timestamp: time,
            is_on: value,
            reason,
          })
        })
      }
    }
  }
}
